import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  CircularProgress,
  Button,
} from '@mui/material';
import {
  Business as BriefcaseIcon,
  Code as CodeIcon,
  Language as GlobeIcon,
  Translate as LanguageIcon,
  Schedule as ClockIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useGlobalConfig } from 'context/GlobalContext';
import { PilotApis } from 'apis/pilotAPI';
import { Gauge } from 'lucide-react';
import { DatabaseZap } from 'lucide-react';
import { Users } from 'lucide-react';
import { useTheme } from '@mui/material/styles';
import { FileText } from 'lucide-react';
import { ServerCog } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { selectProjectAttributes, setProjectAttributes } from 'store/dataReducers/applicationScopeSlice';
import { useToast } from 'context/ToastContext';
import type { ProjectAttributes } from 'types/apis/applicationScopeInterfaces';


interface ProjectAttribute {
  icon: React.ReactNode;
  label: string;
  value: string;
}

interface ProjectAttributesSectionProps {
  projectId: string;
  utilityName: string;
  apiHost?: string; // API host for the pilot/utility
  country?: string;
  language?: string;
  ingestionType?: string;
  timezone?: string;
  customAttributes?: ProjectAttribute[];
  title?: string;
  subtitle?: string;
  description?: string;
}

export const ProjectAttributesSection: React.FC<ProjectAttributesSectionProps> = ({
  projectId,
  utilityName,
  apiHost,
  country: propCountry,
  language: propLanguage,
  ingestionType: propIngestionType,
  timezone: propTimezone,
  customAttributes,
  title = "Project Attributes",
  subtitle,
  description,
}) => {
  const { rc } = useGlobalConfig();
  const theme = useTheme();
  const toast = useToast();
  const dispatch = useDispatch();
  const projectAttributesFromRedux = useSelector(selectProjectAttributes);
  const [metadata, setMetadata] = useState<{ country?: string; timezone?: string; language?: string; fuelType?: string; meterType?: string; customerType?: string; ingestionType?: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isResettingCache, setIsResettingCache] = useState(false);


  // Use extracted metadata values, fall back to props, then to defaults
  // For customer_type, fuel_type, meter_type, country, timezone, and language, prefer Redux values if available
  console.log('projectAttributesFromRedux', projectAttributesFromRedux);
  const country = projectAttributesFromRedux?.country || metadata.country || propCountry || "";
  const timezone = projectAttributesFromRedux?.timezone || metadata.timezone || propTimezone || "";
  const language = projectAttributesFromRedux?.language || metadata.language || propLanguage || "";
  const fuelType = projectAttributesFromRedux?.fuel_type || metadata.fuelType || "";
  const meterType = projectAttributesFromRedux?.meter_type || metadata.meterType || "";
  const customerType = projectAttributesFromRedux?.customer_type || metadata.customerType || "";
  const ingestionType = metadata.ingestionType || propIngestionType || "";


  const defaultAttributes: ProjectAttribute[] = [
    {
      icon: <CodeIcon sx={{ color: 'text.secondary', fontSize: 20, opacity: 0.7 }} />,
      label: 'Pilot ID',
      value: projectId,
    },
    {
      icon: <BriefcaseIcon sx={{ color: 'text.secondary', fontSize: 20, opacity: 0.7 }} />,
      label: 'Project Name',
      value: utilityName,
    },
    {
      icon: <ServerCog style={{ color: `${theme.palette.text.secondary}`, opacity: 0.7 }} size={20} />,
      label: 'Ingestion Type',
      value: ingestionType,
    },
    {
      icon: <GlobeIcon sx={{ color: 'text.secondary', fontSize: 20, opacity: 0.7 }} />,
      label: 'Country',
      value: country,
    },
    {
      icon: <LanguageIcon sx={{ color: 'text.secondary', fontSize: 20, opacity: 0.7 }} />,
      label: 'Language',
      value: language,
    },
    {
      icon: <Users style={{ color: `${theme.palette.text.secondary}`, opacity: 0.7 }} size={20} />,
      label: 'Customer Type',
      value: customerType,
    },
    {
      icon: <DatabaseZap style={{ color: `${theme.palette.text.secondary}`, opacity: 0.7 }} size={20} />,
      label: 'Fuel Type',
      value: fuelType,
    },
    {
      icon: <Gauge style={{ color: `${theme.palette.text.secondary}`, opacity: 0.7 }} size={20} />,
      label: 'Meter Type',
      value: meterType,
    },
    {
      icon: <ClockIcon sx={{ color: 'text.secondary', fontSize: 20, opacity: 0.7 }} />,
      label: 'Time Zone',
      value: timezone,
    },
  ];

  const attributes = customAttributes || defaultAttributes;
  const displaySubtitle = subtitle || `Core project information`;
  const defaultDescription = description;

  const handleResetCache = async () => {
    if (!projectId) {
      toast.error('Project ID is required to reset cache');
      return;
    }

    setIsResettingCache(true);
    try {
      const response = await PilotApis.resetPilotCache(rc, projectId);
      
      if (response.success) {
        // Clear Redux project attributes cache so fresh data will be fetched
        const emptyProjectAttributes: ProjectAttributes = {
          customer_type: null,
          fuel_type: null,
          meter_type: null,
          country: null,
          timezone: null,
          language: null,
          supported_locale: null
        };
        dispatch(setProjectAttributes(emptyProjectAttributes));
        
        // Refetch project attributes to get fresh data
        try {
          const attributesResponse = await PilotApis.getProjectAttributes(rc, projectId);
          if (attributesResponse.success && attributesResponse.data) {
            const { 
              allowed_measurement_type, 
              supported_meter_types, 
              default_user_segment,
              country,
              default_timezone,
              primary_language,
              supported_locale
            } = attributesResponse.data?.attributes;
            const refreshedAttributes: ProjectAttributes = {
              customer_type: default_user_segment || null,
              fuel_type: allowed_measurement_type || null,
              meter_type: supported_meter_types 
                ? (typeof supported_meter_types === 'string' 
                    ? (() => {
                        try {
                          const parsed = JSON.parse(supported_meter_types);
                          return Array.isArray(parsed) ? parsed.join(', ') : String(parsed);
                        } catch {
                          return supported_meter_types;
                        }
                      })()
                    : Array.isArray(supported_meter_types) 
                      ? supported_meter_types.join(', ')
                      : String(supported_meter_types))
                : null,
              country: country || null,
              timezone: default_timezone || null,
              language: primary_language || null,
              supported_locale: supported_locale ?? null,
            };
            dispatch(setProjectAttributes(refreshedAttributes));
          }
        } catch (fetchError) {
          console.error('Error refetching project attributes after cache reset:', fetchError);
          // Don't show error toast here as cache reset was successful
        }
        
        toast.success(`Cache reset successfully for project ${projectId}. Project attributes refreshed.`);
      } else {
        const errorMessage = response.error?.message || response.error || 'Failed to reset cache';
        toast.error(errorMessage);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      toast.error(`Error resetting cache: ${errorMessage}`);
    } finally {
      setIsResettingCache(false);
    }
  };

  if (isLoading) {
    return (
      <Card sx={{ 
        bgcolor: 'background.paper', 
        boxShadow: 2, 
        borderRadius: 3,
        mb: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderOpacity: 0.1
      }}>
        <CardContent sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  // Always show basic project info (project ID and utility name) even on error
  const basicAttributes: ProjectAttribute[] = [
    {
      icon: <CodeIcon sx={{ color: 'text.secondary', fontSize: 20, opacity: 0.7 }} />,
      label: 'Pilot ID',
      value: projectId,
    },
    {
      icon: <BriefcaseIcon sx={{ color: 'text.secondary', fontSize: 20, opacity: 0.7 }} />,
      label: 'Utility Name',
      value: utilityName,
    },
  ];

  if (error) {
    return (
      <Card sx={{ 
        bgcolor: 'background.paper', 
        boxShadow: 2, 
        borderRadius: 3,
        mb: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderOpacity: 0.1
      }}>
        <CardContent sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1, mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <BriefcaseIcon sx={{ mr: 2.5, color: 'primary.main', fontSize: 28 }} />
                <Typography variant="h6" component="h2" sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 0,
                  fontSize: '1.125rem'
                }}>
                  {title}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={isResettingCache ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={handleResetCache}
                disabled={isResettingCache || !projectId}
                sx={{
                  minWidth: 140,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                }}
              >
                {isResettingCache ? 'Fetching...' : 'Refresh'}
              </Button>
            </Box>
            <Typography variant="body2" sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',
              lineHeight: 1.4,
              textAlign: 'left'
            }}>
              {displaySubtitle}
            </Typography>
          </Box>

          {/* Basic Attributes (always shown) */}
          <Box>
            {basicAttributes.map((attribute, index) => (
              <React.Fragment key={attribute.label}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  py: 2,
                  px: 0
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    {attribute.icon}
                    <Typography variant="body2" sx={{ 
                      ml: 2, 
                      color: 'text.primary',
                      fontWeight: 500,
                      fontSize: '0.875rem'
                    }}>
                      {attribute.label}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ 
                    color: 'text.secondary',
                    fontWeight: 400,
                    fontSize: '0.875rem'
                  }}>
                    {attribute.value}
                  </Typography>
                </Box>
                {index < basicAttributes.length - 1 && (
                  <Divider sx={{ 
                    borderColor: 'divider',
                    opacity: 0.3,
                    mx: 0
                  }} />
                )}
              </React.Fragment>
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ 
      bgcolor: 'background.paper', 
      boxShadow: 2, 
      borderRadius: 3,
      mb: 2,
      border: '1px solid',
      borderColor: 'divider',
      borderOpacity: 0.1
    }}>
      <CardContent sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1, mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <BriefcaseIcon sx={{ mr: 2.5, color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h6" component="h2" sx={{
                fontWeight: 600,
                color: 'text.primary',
                mb: 0,
                fontSize: '1.125rem'
              }}>
                {title}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={isResettingCache ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={handleResetCache}
              disabled={isResettingCache || !projectId}
              sx={{
                minWidth: 140,
                textTransform: 'none',
                fontSize: '0.875rem',
              }}
            >
              {isResettingCache ? 'Fetching...' : 'Refresh'}
            </Button>
          </Box>
          <Typography variant="body2" sx={{
            color: 'text.secondary',
            fontSize: '0.875rem',
            lineHeight: 1.4,
            textAlign: 'left'
          }}>
            {displaySubtitle}
          </Typography>
        </Box>

        {/* Attributes List - Two Column Layout */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          gap: 4 
        }}>
          {/* First Column - First 5 attributes */}
          <Box sx={{ flex: 1 }}>
            {attributes.slice(0, 5).map((attribute, index) => (
              <React.Fragment key={attribute.label}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  py: 2,
                  px: 0
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    {attribute.icon}
                    <Typography variant="body2" sx={{ 
                      ml: 2, 
                      color: 'text.primary',
                      fontWeight: 500,
                      fontSize: '0.875rem'
                    }}>
                      {attribute.label}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ 
                    color: 'text.secondary',
                    fontWeight: 400,
                    fontSize: '0.875rem'
                  }}>
                    {attribute.value}
                  </Typography>
                </Box>
                {index < 4 && (
                  <Divider sx={{ 
                    borderColor: 'divider',
                    opacity: 0.3,
                    mx: 0
                  }} />
                )}
              </React.Fragment>
            ))}
          </Box>

          {/* Second Column - Remaining attributes */}
          {attributes.length > 6 && (
            <Box sx={{ flex: 1 }}>
              {attributes.slice(5).map((attribute, index) => (
                <React.Fragment key={attribute.label}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    py: 2,
                    px: 0
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      {attribute.icon}
                      <Typography variant="body2" sx={{ 
                        ml: 2, 
                        color: 'text.primary',
                        fontWeight: 500,
                        fontSize: '0.875rem'
                      }}>
                        {attribute.label}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ 
                      color: 'text.secondary',
                      fontWeight: 400,
                      fontSize: '0.875rem'
                    }}>
                      {attribute.value}
                    </Typography>
                  </Box>
                  {index < attributes.slice(5).length - 1 && (
                    <Divider sx={{ 
                      borderColor: 'divider',
                      opacity: 0.3,
                      mx: 0
                    }} />
                  )}
                </React.Fragment>
              ))}
            </Box>
          )}
        </Box>

        {/* Project Description Section */}
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FileText style={{ color: `${theme.palette.text.secondary}`, opacity: 0.7, marginRight: 8 }} size={20} />
            <Typography variant="body2" sx={{ 
              color: 'text.primary',
              fontWeight: 500,
              fontSize: '0.875rem'
            }}>
              Project Description
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ 
            color: 'text.secondary',
            fontSize: '0.875rem',
            lineHeight: 1.6,
            textAlign: 'left',
          }}>
            {defaultDescription}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}; 